import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getUnifiedSales, getBranches, getDeliveryProof, getPOSTerminals } from '../../services/api';
import { Receipt, Search, Calendar, Eye, ChevronLeft, ChevronRight, RefreshCw, Filter, X, Download, Camera, Truck } from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_LABELS = {
  comanda_pro: 'Comanda Pro',
  local_pos: 'Local POS',
  marketplace: 'Marketplace',
  online: 'Online',
  pos: 'POS Local',
};

const SOURCE_COLORS = {
  comanda_pro: 'bg-blue-100 text-blue-700',
  local_pos: 'bg-purple-100 text-purple-700',
  marketplace: 'bg-amber-100 text-amber-700',
  online: 'bg-green-100 text-green-700',
  pos: 'bg-indigo-100 text-indigo-700',
};

const PAYMENT_LABELS = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  TRANSFER: 'Transferencia',
  cash: 'Efectivo',
  card: 'Tarjeta',
  transfer: 'Transferencia',
  cash_on_pickup: 'Efectivo al recoger',
  cash_on_delivery: 'Efectivo al recibir',
};

const DELIVERY_LABELS = {
  delivery: 'Delivery',
  pickup: 'Recoger en local',
};

const DELIVERY_COLORS = {
  delivery: 'bg-orange-100 text-orange-700',
  pickup: 'bg-cyan-100 text-cyan-700',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const Ventas = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [branches, setBranches] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [detail, setDetail] = useState(null);
  const [deliveryProof, setDeliveryProof] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    branch_id: '',
    terminal_id: '',
    source: '',
    facturado: '',
    delivery_type: '',
  });

  useEffect(() => {
    if (user?.business_id) {
      getBranches(user.business_id).then(r => setBranches(r.data || [])).catch(() => {});
    }
  }, [user?.business_id]);

  useEffect(() => {
    if (filters.branch_id) {
      getPOSTerminals(filters.branch_id).then(r => setTerminals(r.data || [])).catch(() => setTerminals([]));
    } else {
      setTerminals([]);
      if (filters.terminal_id) setFilters(f => ({ ...f, terminal_id: '' }));
    }
  }, [filters.branch_id]);

  const loadSales = useCallback(async (page = 1) => {
    if (!user?.business_id) return;
    setLoading(true);
    try {
      const params = {
        business_id: user.business_id,
        skip: (page - 1) * 50,
        limit: 50,
      };
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date + 'T23:59:59';
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.terminal_id) params.terminal_id = filters.terminal_id;
      if (filters.source) params.source = filters.source;
      if (filters.delivery_type) params.delivery_type = filters.delivery_type;
      if (filters.facturado !== '') params.facturado = filters.facturado === 'true';

      const { data } = await getUnifiedSales(params);
      const salesList = Array.isArray(data) ? data : (data.sales || []);
      setSales(salesList);

      const total = data.pagination?.total || salesList.length;
      const pages = data.pagination?.pages || 1;
      setPagination({ total, page, pages });
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar ventas');
    }
    setLoading(false);
  }, [user?.business_id, filters]);

  useEffect(() => { loadSales(); }, [loadSales]);

  const updateFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const openDetail = async (sale) => {
    setDetail(sale);
    setDeliveryProof(null);
    // Auto-load delivery proof for marketplace delivery orders
    if (sale.source === 'marketplace' && sale.delivery_type === 'delivery') {
      // If proof_photo_url is already in the sale data, use it
      if (sale.proof_photo_url) {
        setDeliveryProof({
          proof_photo_url: sale.proof_photo_url,
          delivery_notes: sale.delivery_notes || '',
          driver_name: sale.driver_name || '',
          delivered_at: sale.delivered_at,
        });
      } else {
        // Fetch from API
        setLoadingProof(true);
        try {
          const { data } = await getDeliveryProof(sale.id);
          setDeliveryProof(data);
        } catch {
          // No proof available
        }
        setLoadingProof(false);
      }
    }
  };

  const filteredSales = sales;

  // Summary stats
  const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.total || 0), 0);
  const totalCount = filteredSales.length;
  const facturadasCount = filteredSales.filter(s => s.facturado).length;

  const handleExportCSV = () => {
    if (!filteredSales.length) return;
    const headers = ['Ticket', 'Fecha', 'Origen', 'Entrega', 'Total', 'Pago', 'Facturada', 'Sucursal'];
    const rows = filteredSales.map(s => [
      s.ticket_number || s.id?.slice(0, 8),
      s.created_at,
      SOURCE_LABELS[s.source] || s.source || 'POS',
      s.source === 'marketplace' ? (DELIVERY_LABELS[s.delivery_type] || '-') : '-',
      (s.total || 0).toFixed(2),
      PAYMENT_LABELS[s.payment_method] || s.payment_method,
      s.facturado ? 'Si' : 'No',
      s.branch_name || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventas-${filters.start_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user?.business_id) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 text-slate-500">
          <p>No se encontro un negocio asociado a tu cuenta.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Historial de Ventas</h1>
            <p className="text-slate-500 text-sm mt-1">
              Todas las ventas de todos los canales: POS, Comanda Pro, Marketplace
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExportCSV} className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-600">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={() => loadSales(pagination.page)} disabled={loading} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">Total Ventas</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">Tickets</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{totalCount}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">Facturadas</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{facturadasCount} <span className="text-sm font-normal text-slate-400">de {totalCount}</span></p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Desde</label>
              <input type="date" className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-40" value={filters.start_date}
                onChange={e => updateFilter('start_date', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
              <input type="date" className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-40" value={filters.end_date}
                onChange={e => updateFilter('end_date', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Origen</label>
              <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-44" value={filters.source} onChange={e => updateFilter('source', e.target.value)}>
                <option value="">Todos</option>
                <option value="comanda_pro">Comanda Pro</option>
                <option value="pos">POS Local</option>
                <option value="marketplace">Marketplace</option>
                <option value="online">Online</option>
              </select>
            </div>
            {branches.length > 1 && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Sucursal</label>
                <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-44" value={filters.branch_id} onChange={e => updateFilter('branch_id', e.target.value)}>
                  <option value="">Todas</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}
            {terminals.length > 0 && (
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Caja</label>
                <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-44" value={filters.terminal_id} onChange={e => updateFilter('terminal_id', e.target.value)}>
                  <option value="">Todas</option>
                  {terminals.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Entrega</label>
              <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-44" value={filters.delivery_type} onChange={e => updateFilter('delivery_type', e.target.value)}>
                <option value="">Todos</option>
                <option value="delivery">Delivery</option>
                <option value="pickup">Recoger en local</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Facturado</label>
              <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm w-36" value={filters.facturado} onChange={e => updateFilter('facturado', e.target.value)}>
                <option value="">Todos</option>
                <option value="true">Facturadas</option>
                <option value="false">Sin facturar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 font-semibold text-slate-600">Ticket</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Fecha</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Origen</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Entrega</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Sucursal</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Caja</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Total</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Pago</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Factura</th>
                  <th className="text-center p-3 font-semibold text-slate-600 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center p-8 text-slate-400">Cargando ventas...</td></tr>
                )}
                {!loading && filteredSales.length === 0 && (
                  <tr><td colSpan={10} className="text-center p-8 text-slate-400">Sin ventas en este periodo</td></tr>
                )}
                {!loading && filteredSales.map((sale) => {
                  const source = sale.source || 'pos';
                  return (
                    <tr key={sale.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-mono text-xs font-medium">
                        {sale.ticket_number || (sale.id || '').slice(0, 8)}
                      </td>
                      <td className="p-3 text-xs text-slate-600">{formatDate(sale.created_at)}</td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_COLORS[source] || 'bg-slate-100 text-slate-600'}`}>
                          {SOURCE_LABELS[source] || source}
                        </span>
                        {sale.marketplace_order_number && (
                          <span className="block text-[10px] text-slate-400 mt-0.5">{sale.marketplace_order_number}</span>
                        )}
                      </td>
                      <td className="p-3">
                        {sale.delivery_type ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${DELIVERY_COLORS[sale.delivery_type] || 'bg-slate-100 text-slate-600'}`}>
                            {DELIVERY_LABELS[sale.delivery_type] || sale.delivery_type}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-slate-600">{sale.branch_name || '-'}</td>
                      <td className="p-3 text-xs text-slate-600">{sale.register_name || '-'}</td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(sale.total)}</td>
                      <td className="p-3 text-xs">{PAYMENT_LABELS[sale.payment_method] || sale.payment_method || '-'}</td>
                      <td className="p-3 text-center">
                        {sale.facturado ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">CFDI</span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <button onClick={() => openDetail(sale)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-slate-200 bg-slate-50">
              <span className="text-xs text-slate-500">
                {pagination.total} ventas | Pagina {pagination.page} de {pagination.pages}
              </span>
              <div className="flex gap-1">
                <button className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-40" disabled={pagination.page <= 1} onClick={() => loadSales(pagination.page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button className="p-2 rounded-lg hover:bg-slate-200 disabled:opacity-40" disabled={pagination.page >= pagination.pages} onClick={() => loadSales(pagination.page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setDetail(null); setDeliveryProof(null); }}>
          <div className="bg-white rounded-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">Detalle de Venta</h3>
              <button onClick={() => { setDetail(null); setDeliveryProof(null); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Sale info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Ticket:</span>{' '}
                  <span className="font-mono font-medium">{detail.ticket_number || (detail.id || '').slice(0, 8)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Origen:</span>{' '}
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SOURCE_COLORS[detail.source] || 'bg-slate-100 text-slate-600'}`}>
                    {SOURCE_LABELS[detail.source] || detail.source || 'POS'}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Fecha:</span>{' '}
                  <span>{formatDate(detail.created_at)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Pago:</span>{' '}
                  <span>{PAYMENT_LABELS[detail.payment_method] || detail.payment_method}</span>
                </div>
                {detail.delivery_type && (
                  <div>
                    <span className="text-slate-500">Entrega:</span>{' '}
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${DELIVERY_COLORS[detail.delivery_type] || 'bg-slate-100 text-slate-600'}`}>
                      {DELIVERY_LABELS[detail.delivery_type] || detail.delivery_type}
                    </span>
                  </div>
                )}
                {detail.branch_name && (
                  <div className={detail.delivery_type ? '' : 'col-span-2'}>
                    <span className="text-slate-500">Sucursal:</span>{' '}
                    <span>{detail.branch_name}</span>
                  </div>
                )}
              </div>

              {/* Marketplace customer info */}
              {detail.source === 'marketplace' && detail.customer_name && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
                  <span className="text-xs font-bold text-amber-700 block mb-1">Cliente Marketplace</span>
                  <div><span className="text-slate-500">Nombre:</span> {detail.customer_name}</div>
                  {detail.customer_phone && <div><span className="text-slate-500">Tel:</span> {detail.customer_phone}</div>}
                  {detail.delivery_type === 'delivery' && detail.customer_address && (
                    <div><span className="text-slate-500">Dirección:</span> {detail.customer_address}</div>
                  )}
                </div>
              )}

              {/* Items */}
              {detail.items && detail.items.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-500 mb-2">Productos</h4>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    {detail.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-700">{item.quantity}x {item.product_name}</span>
                        <span className="font-medium">{formatCurrency(item.subtotal || (item.unit_price * item.quantity))}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              <div className="border-t border-slate-200 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal:</span>
                  <span>{formatCurrency(detail.subtotal)}</span>
                </div>
                {detail.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Descuento:</span>
                    <span className="text-red-600">-{formatCurrency(detail.discount)}</span>
                  </div>
                )}
                {detail.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Envío:</span>
                    <span>{formatCurrency(detail.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">IVA:</span>
                  <span>{formatCurrency(detail.tax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-1 border-t border-slate-200">
                  <span>Total:</span>
                  <span>{formatCurrency(detail.total)}</span>
                </div>
              </div>

              {/* Invoice info */}
              {detail.facturado && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                  Facturada {detail.factura_uuid && <span className="font-mono block mt-1">UUID: {detail.factura_uuid}</span>}
                </div>
              )}

              {detail.notes && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                  <span className="text-xs font-bold text-slate-500 block mb-1">Notas:</span>
                  {detail.notes}
                </div>
              )}

              {/* Delivery proof section */}
              {detail.source === 'marketplace' && detail.delivery_type === 'delivery' && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
                    <Camera className="w-4 h-4" />
                    Evidencia de Entrega
                  </h4>
                  {loadingProof ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                      <span className="ml-2 text-sm text-slate-500">Cargando evidencia...</span>
                    </div>
                  ) : deliveryProof ? (
                    <div className="space-y-3">
                      <div
                        className="relative rounded-lg overflow-hidden border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setShowPhotoModal(true)}
                      >
                        <img
                          src={deliveryProof.proof_photo_url}
                          alt="Evidencia de entrega"
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                          <span className="opacity-0 hover:opacity-100 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full transition-opacity">
                            Ver completa
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {deliveryProof.driver_name && (
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Truck className="w-3.5 h-3.5" />
                            <span>{deliveryProof.driver_name}</span>
                          </div>
                        )}
                        {deliveryProof.delivered_at && (
                          <div className="text-slate-500">
                            {formatDate(deliveryProof.delivered_at)}
                          </div>
                        )}
                      </div>
                      {deliveryProof.delivery_notes && (
                        <div className="bg-slate-50 rounded-lg p-2 text-xs text-slate-600">
                          <span className="font-medium text-slate-500">Nota del repartidor: </span>
                          {deliveryProof.delivery_notes}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      <Camera className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>Sin evidencia de entrega disponible</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full-screen photo modal */}
      {showPhotoModal && deliveryProof?.proof_photo_url && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" onClick={() => setShowPhotoModal(false)}>
          <div className="relative max-w-3xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowPhotoModal(false)}
              className="absolute -top-10 right-0 p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={deliveryProof.proof_photo_url}
              alt="Evidencia de entrega"
              className="w-full rounded-lg shadow-2xl"
            />
            {deliveryProof.driver_name && (
              <p className="text-center text-white/80 text-sm mt-3">
                Entregado por: {deliveryProof.driver_name}
                {deliveryProof.delivered_at && ` — ${formatDate(deliveryProof.delivered_at)}`}
              </p>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Ventas;
